import type { BimContractData, BimContractObjectData } from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import { formatNumberToRu } from 'utils';
import { replacerCost } from 'utils/replacerCost';

import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { useBimCreateContractObject } from '../BimContractStructure/hooks/useBimCreateContractObject';
import type { ItemTableContractObject } from '../BimContractStructure/hooks/useBimItemsContractStructureTable';
import { useBimUpdateContractObject } from '../BimContractStructure/hooks/useBimUpdateContractObject';

import type { FieldValuesBimCreateStructure } from './BimCreateStructureObject';

interface IUseContractObject {
    contractId: number;
    onClose: () => void;
    constructionTypeId?: number;
    contractObject?: ItemTableContractObject;
    seasons?: BimContractData['seasons'];
    isUseContract?: boolean;
    onSuccess: (item: BimContractObjectData) => void;
    refreshProject: () => void;
    bimContractObjectsMakeRequest: () => void;
}

export const useBimContractObject = (props: IUseContractObject) => {
    const dispatch = useDispatch();

    const {
        contractObject,
        contractId,
        constructionTypeId,
        seasons,
        isUseContract,
        onClose,
        onSuccess,
        refreshProject,
        bimContractObjectsMakeRequest,
    } = props;

    const bimCreateContractObjectRequest = useBimCreateContractObject({
        onSuccess,
        onClose,
        refreshProject,
        bimContractObjectsMakeRequest,
    });

    const bimUpdateContractObjectRequest = useBimUpdateContractObject({ onSuccess, onClose });

    const defaultValuesStructureContract: FieldValuesBimCreateStructure = useMemo(() => {
        if (isUseContract && contractObject) {
            return Object.entries(contractObject)?.reduce((acc: any, [key, value]) => {
                if (key.includes('season')) {
                    acc = { ...acc, [key]: formatNumberToRu(value, 2) };
                }

                if (key === 'title') {
                    acc = { ...acc, objectName: value };
                }

                if (key === 'hasGuaranteedVolume') {
                    acc = { ...acc, hasGuaranteedVolume: value === 'Да' };
                }

                return acc;
            }, {});
        }

        return undefined;
    }, [contractObject, isUseContract]);

    const onSubmitStructureContract = useCallback(
        (formData) => {
            if (isUseContract) {
                // TODO: any
                const transformData: any = Object.entries(formData).reduce((acc: AnyObject, [key, value]) => {
                    if (key.includes('season')) {
                        const seasonObject = {
                            contractSeasonId: seasons?.filter((season) => Number(key.split('_')[1]) === season.year)[0]
                                ?.id,
                            seasonValue: replacerCost(value as string),
                        };

                        acc = {
                            ...acc,
                            objectSeasonDistributions: acc.objectSeasonDistributions
                                ? [...acc.objectSeasonDistributions, seasonObject]
                                : [seasonObject],
                        };
                    }

                    if (key === 'objectName') {
                        acc = { ...acc, name: value };
                    }

                    if (key === 'hasGuaranteedVolume') {
                        acc = { ...acc, hasGuaranteedVolume: value || false };
                    }

                    return acc;
                }, {});

                const variables = {
                    contractObjectInput: defaultValuesStructureContract
                        ? { ...transformData, id: contractObject?.id }
                        : { ...transformData, contractId, constructionTypeId },
                };

                if (defaultValuesStructureContract) {
                    dispatch(bimUpdateContractObjectRequest(variables));
                } else {
                    dispatch(bimCreateContractObjectRequest(variables));
                }
            }
        },
        [
            bimCreateContractObjectRequest,
            bimUpdateContractObjectRequest,
            constructionTypeId,
            contractId,
            contractObject?.id,
            defaultValuesStructureContract,
            dispatch,
            isUseContract,
            seasons,
        ],
    );

    return {
        defaultValuesStructureContract,
        onSubmitStructureContract,
    };
};
